import {
  ArrowDropDown,
  ArrowDropUp,
  Key,
  Lock,
  Photo,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { UserProfilePictureDialog } from '~/components/Dialogs/UserProfilePictureDialog';
import { UseDialogStateOutput } from '~/hooks/useDialogState';
import { useMenu } from '~/hooks/useMenu';
import { AccountManagementMenu_OnUpdateLegacy } from '~/library/AccountManagementMenu/component';
import { ChangeLoginMethodDialog } from '~/scenes/PeopleModule/components/EmployeeActions/ChangeLoginMethodDialog';
import { CreateUserAccountDialog } from '~/scenes/PeopleModule/components/EmployeeActions/CreateUserAccountDialog';
import { EmployeeActivityLogDialog } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog';

import { ChangeAccountStatus } from './ChangeAccountStatus';
import { ChangeRole } from './ChangeRole';
import { ChangeUserName } from './ChangeUserName';
import { ResetPassword } from './ResetPassword';
import { useEmployeeAction } from './useEmployeeActions';
import { useSxStyles } from './useStyles';

export type EmployeeActionsProps = AccountManagementMenu_OnUpdateLegacy & {
  employeeId: number;
};

export const EmployeeActions = (props: EmployeeActionsProps) => {
  const { employeeId, onUpdateLegacy } = props;
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const {
    dataLoaded,
    user: { isInternal, isExternal },
    permissions: {
      canChangeRoles,
      canResetInternalPassword,
      canViewUserActivity,
      canChangeLoginMethod,
      canViewAccountManagementMenu,
    },
    dialogs: {
      createUserAccountDialog,
      changeRolesDialog,
      changeLoginMethod,
      resetPasswordDialog,
      changeUserNameDialog,
      changeAccountStatusDialog,
      cropImageDialog,
      activityTrackingDialog,
    },
  } = useEmployeeAction({ employeeId });

  const actionsMenu = useMenu();

  const EmployeeActionsMenuItem = (menuProps: {
    dialogState: UseDialogStateOutput;
    label: string;
    showItem?: boolean;
    icon?: ReactNode;
  }) => {
    const { dialogState, label, showItem, icon } = menuProps;
    const isShown = typeof showItem === 'undefined' || showItem;

    return isShown ? (
      <MenuItem
        onClick={() => {
          actionsMenu.close();
          dialogState.open();
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </MenuItem>
    ) : null;
  };

  const handleCloseDialog = (dialogState: UseDialogStateOutput) => {
    dialogState.close();
  };

  return canViewAccountManagementMenu && dataLoaded ? (
    <>
      <Button
        onClick={(event) => {
          if (dataLoaded) {
            actionsMenu.open(event);
          }
        }}
        variant="contained"
        sx={sxStyles.menuButton}
        endIcon={actionsMenu.isOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        disableElevation
      >
        {t('laAccountMgmt')}
      </Button>

      <Menu
        anchorEl={actionsMenu.anchorEl}
        open={actionsMenu.isOpen}
        onClose={() => {
          actionsMenu.close();
        }}
        slotProps={{
          list: {
            sx: sxStyles.menuList,
          },
        }}
      >
        <EmployeeActionsMenuItem
          label={t('laCreateUserAccount')}
          dialogState={createUserAccountDialog}
          icon={<Lock />}
          showItem={isExternal}
        />
        <EmployeeActionsMenuItem
          label={t('laChangeRole')}
          dialogState={changeRolesDialog}
          showItem={canChangeRoles}
        />
        <EmployeeActionsMenuItem
          label={t('laChangeAccountStatus')}
          dialogState={changeAccountStatusDialog}
          icon={<Lock />}
          showItem={isInternal}
        />
        {isInternal && <Divider />}
        <EmployeeActionsMenuItem
          label={t('laResetPassword')}
          dialogState={resetPasswordDialog}
          icon={<Key />}
          showItem={canResetInternalPassword}
        />
        <EmployeeActionsMenuItem
          label={t('laChangeUsername')}
          dialogState={changeUserNameDialog}
          showItem={isInternal}
        />
        <EmployeeActionsMenuItem
          label={t('laChangeLoginMethod')}
          dialogState={changeLoginMethod}
          showItem={canChangeLoginMethod}
        />
        <Divider />
        <EmployeeActionsMenuItem
          label={t('laPicture')}
          dialogState={cropImageDialog}
          icon={<Photo />}
        />
        {canViewUserActivity && <Divider />}
        <EmployeeActionsMenuItem
          label={t('userActivityLog')}
          dialogState={activityTrackingDialog}
          showItem={canViewUserActivity}
        />
      </Menu>
      {dataLoaded && isInternal && (
        <>
          <ChangeRole
            open={changeRolesDialog.isOpen}
            onClose={() => handleCloseDialog(changeRolesDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <ChangeLoginMethodDialog
            employeeId={employeeId}
            open={changeLoginMethod.isOpen}
            onClose={() => handleCloseDialog(changeLoginMethod)}
            onUpdateLegacy={onUpdateLegacy}
          />
          {/* TODO: Add Change Login Method dialog */}
          <ResetPassword
            open={resetPasswordDialog.isOpen}
            onClose={() => handleCloseDialog(resetPasswordDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <ChangeUserName
            open={changeUserNameDialog.isOpen}
            onClose={() => handleCloseDialog(changeUserNameDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <ChangeAccountStatus
            open={changeAccountStatusDialog.isOpen}
            onClose={() => handleCloseDialog(changeAccountStatusDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <EmployeeActivityLogDialog
            employeeId={employeeId}
            open={activityTrackingDialog.isOpen}
            onClose={() => handleCloseDialog(activityTrackingDialog)}
          />
        </>
      )}
      {dataLoaded && isExternal && (
        <CreateUserAccountDialog
          employeeId={employeeId}
          open={createUserAccountDialog.isOpen}
          onClose={() => handleCloseDialog(createUserAccountDialog)}
        />
      )}
      {dataLoaded && (
        <UserProfilePictureDialog
          employeeId={employeeId}
          open={cropImageDialog.isOpen}
          onClose={() => handleCloseDialog(cropImageDialog)}
          onUpdateLegacy={onUpdateLegacy}
        />
      )}
    </>
  ) : null;
};
